import type { MigrationDevice } from '../api/migrations';

// FD-503 — the canary device sorts FIRST, always.
//
// Under FD-ADR-035 decision 4 the first device to fetch a flight plan becomes
// the wave's canary, and EVERY OTHER DEVICE HOLDS AT `409 CANARY_HOLD` until it
// completes. So one row in this table is not one device among N: it is the row
// the whole wave is waiting on, and a canary that salvages or faults keeps the
// wave held until an operator releases it by an audited action.
//
// A row like that being twelfth in serial order — or worse, on page two — is the
// table failing at the one moment it matters most. Pinning it is the cheapest
// possible fix and costs nothing when there is no canary.
//
// ── THREE PROPERTIES, AND EACH OF THEM IS A TEST ───────────────────────────
//
//   IT NEVER INJECTS. A canary filtered out by the operator's own filters stays
//   filtered out. Re-adding it would make a filter lie about what it is showing,
//   and "show me only failed devices" must mean exactly that — the pin is an
//   ORDERING rule, not a visibility one.
//
//   IT IS STABLE. Everything that is not the canary keeps the order it arrived
//   in, so the pin cannot be mistaken for a sort the operator did not ask for.
//
//   IT IS A NO-OP WITHOUT A CANARY. `canarySerial` is `null` until the first
//   delivery stamps it and absent entirely on a Manager that predates FD-371 —
//   both are "no canary", and neither may reorder anything.
export function pinCanaryFirst(
  devices: MigrationDevice[],
  canarySerial: string | null | undefined,
): MigrationDevice[] {
  const target = (canarySerial ?? '').trim().toUpperCase();
  if (!target) return devices;

  const index = devices.findIndex((d) => (d.serialNumber ?? '').trim().toUpperCase() === target);
  // Not in this list: filtered out, or a canary stamped on a different
  // campaign's row. Either way, nothing to move and nothing to add.
  if (index <= 0) return devices;

  const pinned = devices[index];
  return [pinned, ...devices.slice(0, index), ...devices.slice(index + 1)];
}

/** True when this row is the wave's canary. Case- and whitespace-insensitive. */
export function isCanaryDevice(
  serialNumber: string,
  canarySerial: string | null | undefined,
): boolean {
  const target = (canarySerial ?? '').trim().toUpperCase();
  if (!target) return false;
  return (serialNumber ?? '').trim().toUpperCase() === target;
}
